"use client";

import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Define interfaces for proper typing
interface Sparkle {
  id: string;
  left: number;
  top: number;
  size: number;
  opacity: number;
  scale: number;
  rotation: number;
  duration: number;
  createdAt: number;
  lastPositionUpdate: number;
}

interface HoverStar {
  id: string;
  left: number;
  top: number;
  size: number;
  delay: number;
  duration: number;
  vx: number;
  vy: number;
}

interface ConstellationLine {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  opacity: number;
  width: number;
}

type FancyButtonProps = {
  title: string;
};

// Configuration
const config = {
  maxVisibleSparkles: 5, // Increased for better constellation effect
  maxTotalSparkles: 10, // Increased total number for more variety
  minSparkleSize: 0.5, // Reduced minimum size to 0.5px
  maxSparkleSize: 2, // Reduced maximum size to 1.5px
  minSparkleInterval: 2000, // 2 seconds (for testing - would be 30000 in production)
  maxSparkleInterval: 5000, // 5 seconds (for testing - would be 90000 in production)
  sparkleAnimationDuration: 2000, // ms - increased for better cycling visibility
  sparkleLifetime: 8000, // ms - how long a sparkle stays visible (multiple animation cycles)
  positionUpdateInterval: 2000, // ms - how often to update sparkle positions
  hoverTransitionDuration: 500, // ms
  positionPrecision: 10, // How precise to track positions (higher = more unique positions)
  minDistanceFromPrevious: 25, // Minimum pixel distance from previous positions
  minLineWidth: 0.25, // Minimum line width in px
  maxLineWidth: 0.75, // Maximum line width in px
  maxLineDistance: 60, // Maximum distance to draw a line between sparkles
  hoverMaxLineDistance: 25, // Maximum distance to draw a line between hover stars
  sparkleVariations: [
    { opacity: 0.7, scale: 1.0 },
    { opacity: 0.8, scale: 1.1 },
    { opacity: 0.9, scale: 0.9 },
    { opacity: 1.0, scale: 1.2 },
  ],
  // Define zones to ensure sparkles appear throughout the button
  zones: [
    { xMin: 0, xMax: 0.33, yMin: 0, yMax: 0.33 }, // Top-left
    { xMin: 0.33, xMax: 0.66, yMin: 0, yMax: 0.33 }, // Top-center
    { xMin: 0.66, xMax: 1, yMin: 0, yMax: 0.33 }, // Top-right
    { xMin: 0, xMax: 0.33, yMin: 0.33, yMax: 0.66 }, // Middle-left
    { xMin: 0.33, xMax: 0.66, yMin: 0.33, yMax: 0.66 }, // Middle-center
    { xMin: 0.66, xMax: 1, yMin: 0.33, yMax: 0.66 }, // Middle-right
    { xMin: 0, xMax: 0.33, yMin: 0.66, yMax: 1 }, // Bottom-left
    { xMin: 0.33, xMax: 0.66, yMin: 0.66, yMax: 1 }, // Bottom-center
    { xMin: 0.66, xMax: 1, yMin: 0.66, yMax: 1 }, // Bottom-right
  ],
};

// Styles
const styles = `
/* Base button styles */
.sparkle-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2.25rem;
  padding: 0.5rem 1rem;
  overflow: hidden;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: rgb(37, 99, 235);
  border-radius: 0.375rem;
  transition: all ${config.hoverTransitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1);
}

.sparkle-button:hover {
  background-color: rgb(29, 78, 216);
  transform: scale(1.03);
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.5);
}

.sparkle-button:active {
  transform: scale(0.95);
}

/* Gradient overlay */
.gradient-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, transparent, rgba(96, 165, 250, 0.1), transparent);
  background-size: 200% 200%;
  opacity: 0;
  transition: opacity ${config.hoverTransitionDuration}ms ease-in-out;
}

.sparkle-button:hover .gradient-overlay {
  opacity: 1;
  animation: gradient 3s ease-in-out infinite;
}

@keyframes gradient {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

/* Icon styles */
.icon {
  position: relative;
  z-index: 10;
  width: 1rem;
  height: 1rem;
  transition: transform ${config.hoverTransitionDuration}ms cubic-bezier(0.34, 1.56, 0.64, 1);
}

.sparkle-button:hover .icon {
  transform: scale(1.25);
}

.star-part {
  transform-origin: center;
  opacity: 1;
  transition: all ${config.hoverTransitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1);
}

.sparkle-button:hover .main-star {
  animation: smoothTwinkle 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.sparkle-button:hover .small-star-1 {
  animation: smoothTwinkle 1.2s cubic-bezier(0.4, 0, 0.6, 1) 0.2s infinite;
}

.sparkle-button:hover .small-star-2 {
  animation: smoothTwinkle 1.3s cubic-bezier(0.4, 0, 0.6, 1) 0.4s infinite;
}

@keyframes smoothTwinkle {
  0%, 100% { opacity: 1; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1); }
}

/* Sparkle element */
.sparkle {
  position: absolute;
  border-radius: 50%;
  background-color: white;
  pointer-events: none;
  z-index: 5;
  transition: all 0.3s ease-out;
}

/* Constellation SVG */
.constellation-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 4;
}

/* Constellation line */
.constellation-line {
  stroke: white;
  transition: all 0.3s ease-out;
}

/* Hover stars */
.hover-star {
  position: absolute;
  border-radius: 50%;
  background-color: white;
  opacity: 0;
  transform-origin: center;
  z-index: 5;
  animation: smoothTwinkle var(--duration, 1.5s) cubic-bezier(0.4, 0, 0.6, 1) var(--delay, 0s) infinite;
}

/* Constellation Layers */
.constellation-layer {
  position: absolute;
  inset: 0;
  opacity: 0;
  animation: cycleOpacity 8s infinite, float 5s ease-in-out infinite;
  pointer-events: none;
  transition: opacity 0.5s ease;
}

.constellation-layer.hidden {
  opacity: 0 !important;
  animation: none;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

@keyframes cycleOpacity {
  0% { opacity: 0; }
  10% { opacity: 1; }
  33% { opacity: 1; }
  43% { opacity: 0; }
  100% { opacity: 0; }
}
`;

const StaticConstellationsLayer = memo(
  ({
    constellations,
  }: {
    constellations: {
      id: number;
      sparkles: Sparkle[];
      lines: ConstellationLine[];
    }[];
  }) => {
    return (
      <>
        {constellations.map((layer, index) => (
          <div
            key={layer.id}
            className="constellation-layer"
            style={{ animationDelay: `${index * 3}s` }}
          >
            <svg className="constellation-svg">
              {layer.lines.map((line) => (
                <line
                  key={line.id}
                  x1={line.x1}
                  y1={line.y1}
                  x2={line.x2}
                  y2={line.y2}
                  className="constellation-line"
                  stroke="white"
                  strokeWidth={line.width}
                  strokeOpacity={line.opacity}
                />
              ))}
            </svg>
            {layer.sparkles.map((sparkle) => (
              <div
                key={sparkle.id}
                className="sparkle"
                style={{
                  left: `${sparkle.left}px`,
                  top: `${sparkle.top}px`,
                  width: `${sparkle.size}px`,
                  height: `${sparkle.size}px`,
                  opacity: sparkle.opacity,
                  transform: `scale(${sparkle.scale}) rotate(${sparkle.rotation}deg)`,
                }}
              />
            ))}
          </div>
        ))}
      </>
    );
  }
);

const HoverStarsLayer = memo(
  ({
    isHovered,
    buttonRef,
  }: {
    isHovered: boolean;
    buttonRef: React.RefObject<HTMLButtonElement>;
  }) => {
    const [hoverStars, setHoverStars] = useState<HoverStar[]>([]);
    const requestRef = useRef<number>();

    useEffect(() => {
      if (isHovered) {
        const width = buttonRef.current ? buttonRef.current.offsetWidth : 100;
        const height = buttonRef.current ? buttonRef.current.offsetHeight : 40;

        const newHoverStars: HoverStar[] = Array.from(
          { length: 15 },
          (_, i) => ({
            id: `hover-star-${i}`,
            left: Math.random() * width,
            top: Math.random() * height,
            size: Math.random() * 1.5 + 0.5,
            delay: Math.random() * 0.5,
            duration: Math.random() * 0.6 + 1.2,
            vx: (Math.random() - 0.5) * 0.2,
            vy: (Math.random() - 0.5) * 0.2,
          })
        );
        setHoverStars(newHoverStars);

        const animate = () => {
          setHoverStars((prevStars) => {
            // We use the captured width/height to avoid reading DOM every frame
            // This assumes button size doesn't change while hovering
            return prevStars.map((star) => {
              let newLeft = star.left + star.vx;
              let newTop = star.top + star.vy;
              let newVx = star.vx;
              let newVy = star.vy;

              if (newLeft < 0 || newLeft > width) {
                newVx = -newVx;
                newLeft = Math.max(0, Math.min(newLeft, width));
              }
              if (newTop < 0 || newTop > height) {
                newVy = -newVy;
                newTop = Math.max(0, Math.min(newTop, height));
              }

              return {
                ...star,
                left: newLeft,
                top: newTop,
                vx: newVx,
                vy: newVy,
              };
            });
          });
          requestRef.current = requestAnimationFrame(animate);
        };

        requestRef.current = requestAnimationFrame(animate);

        return () => {
          if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
      } else {
        setHoverStars([]);
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
      }
    }, [isHovered, buttonRef]);
    const hoverConstellationLines = useMemo(() => {
      if (hoverStars.length < 2) return [];

      const lines: ConstellationLine[] = [];

      for (let i = 0; i < hoverStars.length; i++) {
        for (let j = i + 1; j < hoverStars.length; j++) {
          const star1 = hoverStars[i];
          const star2 = hoverStars[j];

          const distance = Math.sqrt(
            Math.pow(star1.left - star2.left, 2) +
              Math.pow(star1.top - star2.top, 2)
          );

          if (distance <= config.hoverMaxLineDistance) {
            const opacity = 0.3 * (1 - distance / config.hoverMaxLineDistance);
            const width =
              Math.random() * (config.maxLineWidth - config.minLineWidth) +
              config.minLineWidth;

            lines.push({
              id: `hover-line-${star1.id}-${star2.id}`,
              x1: star1.left + star1.size / 2,
              y1: star1.top + star1.size / 2,
              x2: star2.left + star2.size / 2,
              y2: star2.top + star2.size / 2,
              opacity,
              width,
            });
          }
        }
      }

      return lines;
    }, [hoverStars]);

    if (!isHovered && hoverStars.length === 0) return null;

    return (
      <>
        {isHovered && hoverStars.length > 1 && (
          <svg className="constellation-svg">
            {hoverConstellationLines.map((line) => (
              <line
                key={line.id}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                className="constellation-line"
                stroke="white"
                strokeWidth={line.width}
                strokeOpacity={line.opacity}
              />
            ))}
          </svg>
        )}

        {hoverStars.map((star) => {
          const starStyle = {
            left: `${star.left}px`,
            top: `${star.top}px`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            "--delay": `${star.delay}s`,
            "--duration": `${star.duration}s`,
          } as React.CSSProperties;
          return <div key={star.id} className="hover-star" style={starStyle} />;
        })}
      </>
    );
  }
);

export const FancyButton: FC<FancyButtonProps> = ({ title }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [constellations, setConstellations] = useState<
    { id: number; sparkles: Sparkle[]; lines: ConstellationLine[] }[]
  >([]);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const generateRandomPosition = useCallback(() => {
    if (!buttonRef.current) return { left: 0, top: 0 };
    const button = buttonRef.current;
    const rect = button.getBoundingClientRect();
    const zone = config.zones[Math.floor(Math.random() * config.zones.length)];
    const xPos =
      (Math.random() * (zone.xMax - zone.xMin) + zone.xMin) * rect.width;
    const yPos =
      (Math.random() * (zone.yMax - zone.yMin) + zone.yMin) * rect.height;
    return { left: xPos, top: yPos };
  }, []);

  const generateConstellationSet = useCallback(
    (setId: number) => {
      if (!buttonRef.current) return null;
      const newSparkles: Sparkle[] = [];
      const count = Math.floor(Math.random() * 3) + 3;

      for (let i = 0; i < count; i++) {
        const position = generateRandomPosition();
        const variation =
          config.sparkleVariations[
            Math.floor(Math.random() * config.sparkleVariations.length)
          ];
        newSparkles.push({
          id: `c${setId}-s${i}`,
          left: position.left,
          top: position.top,
          size:
            Math.random() * (config.maxSparkleSize - config.minSparkleSize) +
            config.minSparkleSize,
          opacity: variation.opacity,
          scale: variation.scale,
          rotation: Math.random() * 360,
          duration: 0,
          createdAt: 0,
          lastPositionUpdate: 0,
        });
      }

      const newLines: ConstellationLine[] = [];
      for (let i = 0; i < newSparkles.length; i++) {
        for (let j = i + 1; j < newSparkles.length; j++) {
          const s1 = newSparkles[i];
          const s2 = newSparkles[j];
          const dist = Math.sqrt(
            Math.pow(s1.left - s2.left, 2) + Math.pow(s1.top - s2.top, 2)
          );
          if (dist <= config.maxLineDistance) {
            newLines.push({
              id: `c${setId}-l${i}-${j}`,
              x1: s1.left + s1.size / 2,
              y1: s1.top + s1.size / 2,
              x2: s2.left + s2.size / 2,
              y2: s2.top + s2.size / 2,
              opacity: 0.2 * (1 - dist / config.maxLineDistance),
              width:
                Math.random() * (config.maxLineWidth - config.minLineWidth) +
                config.minLineWidth,
            });
          }
        }
      }
      return { id: setId, sparkles: newSparkles, lines: newLines };
    },
    [generateRandomPosition]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (buttonRef.current) {
        const sets = [0, 1, 2]
          .map((i) => generateConstellationSet(i))
          .filter(Boolean) as any[];
        setConstellations(sets);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [generateConstellationSet]);

  return (
    <>
      <style>{styles}</style>
      <button
        ref={buttonRef}
        className="sparkle-button"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
      >
        <div className="gradient-overlay"></div>

        {!isHovered && (
          <StaticConstellationsLayer constellations={constellations} />
        )}

        <HoverStarsLayer isHovered={isHovered} buttonRef={buttonRef} />

        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="icon"
        >
          <path
            d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
            className="star-part main-star"
          />
          <g style={{ transformOrigin: "20px 5px" }}>
            <path d="M20 3v4" className="star-part small-star-1" />
            <path d="M22 5h-4" className="star-part small-star-1" />
          </g>
          <g style={{ transformOrigin: "4px 18px" }}>
            <path d="M4 17v2" className="star-part small-star-2" />
            <path d="M5 18H3" className="star-part small-star-2" />
          </g>
        </svg>

        <span style={{ position: "relative", zIndex: 10 }}>{title}</span>
      </button>
    </>
  );
};
